<template>
    <v-card class="fill-height d-flex flex-column" flat>
        <!-- Widget Header -->
        <v-card-title class="pa-2 flex-grow-0 widget-header">
            <v-icon small left :color="getChartIconColor">{{ getChartIcon }}</v-icon>
            <span class="body-2 text-truncate widget-title">{{ widgetName }}</span>
            <v-spacer></v-spacer>

            <!-- Open widget page button -->
            <v-btn
                v-if="!editMode"
                icon
                x-small
                :to="'/analytics/widgets/' + widget.widget_id"
                class="mr-1">
                <v-icon small>mdi-open-in-new</v-icon>
            </v-btn>

            <!-- Filtri utente del widget -->
            <v-menu
                v-if="!editMode && filterDefinitions.length"
                v-model="filterMenu"
                :close-on-content-click="false"
                offset-y
                left
                max-width="420"
                min-width="320">
                <template v-slot:activator="{ on }">
                    <v-badge :value="filtersChanged" dot color="primary" overlap offset-x="10" offset-y="10">
                        <v-btn icon x-small class="mr-1" v-on="on">
                            <v-icon small>mdi-filter-variant</v-icon>
                        </v-btn>
                    </v-badge>
                </template>
                <v-card class="background-2 round-card">
                    <v-card-title class="pa-3">
                        <p class="pl-2 pl-xl-3 mb-0 body-2 font-weight-bold">Filtri</p>
                    </v-card-title>
                    <v-divider></v-divider>
                    <v-card-text class="pa-3">
                        <widget-filters
                            v-model="filterValues"
                            :definitions="filterDefinitions"
                            compact
                            @apply="applyFilters"
                        ></widget-filters>
                    </v-card-text>
                </v-card>
            </v-menu>

            <!-- Refresh button -->
            <v-btn
                v-if="!editMode"
                icon
                x-small
                @click="refreshReport"
                class="mr-1">
                <v-icon small>mdi-refresh</v-icon>
            </v-btn>

            <!-- Remove button in edit mode -->
            <v-btn
                v-if="editMode"
                icon
                x-small
                color="error"
                @click="$emit('remove')">
                <v-icon small>mdi-close</v-icon>
            </v-btn>
        </v-card-title>

        <!-- Filtri attivi: chip cliccabili che aprono il menu dei filtri -->
        <div v-if="activeFilterChips.length" class="d-flex flex-nowrap align-center px-2 pb-1 flex-grow-0 filter-chips-row">
            <v-chip
                v-for="chip in activeFilterChips"
                :key="chip.key"
                x-small
                label
                outlined
                color="primary"
                class="mr-1 filter-chip"
                :title="chip.text"
                @click="filterMenu = true">
                {{ chip.text }}
            </v-chip>
        </div>

        <v-divider></v-divider>

        <!-- Widget Content - Usa WidgetView in modalita compact -->
        <v-card-text class="pa-2 flex-grow-1 overflow-auto widget-content">
            <widget-view
                ref="reportView"
                :report-id="widget.widget_id"
                compact
                @filters-loaded="onFiltersLoaded"
            ></widget-view>
        </v-card-text>
    </v-card>
</template>

<script>
import WidgetView from './WidgetView.vue'
import WidgetFilters from './WidgetFilters.vue'
import { widgetFiltersChanged, widgetFilterChips } from './widgetFilterDefaults'

export default {
    name: 'dashboard-widget-card',

    components: {
        WidgetView,
        WidgetFilters
    },

    props: {
        widget: { type: Object, required: true },
        editMode: { type: Boolean, default: false }
    },

    data() {
        return {
            // Copia locale dei filtri del widget: i $refs non sono reattivi nel template
            filterDefinitions: [],
            filterValues: {},
            filterMenu: false
        }
    },

    computed: {
        widgetName() {
            return this.widget.widget?.name || 'Widget'
        },

        filtersChanged() {
            return widgetFiltersChanged(this.filterDefinitions, this.filterValues)
        },

        activeFilterChips() {
            return widgetFilterChips(this.filterDefinitions, this.filterValues)
        },

        getChartIcon() {
            const type = this.widget.widget?.chart_type
            const icons = {
                counter: 'mdi-counter',
                pie: 'mdi-chart-pie',
                bar: 'mdi-chart-bar',
                line: 'mdi-chart-line',
                column: 'mdi-chart-bar'
            }
            return icons[type] || 'mdi-table'
        },

        getChartIconColor() {
            const type = this.widget.widget?.chart_type
            if (!type) return 'grey'
            return 'primary'
        }
    },

    methods: {
        refreshReport() {
            if (this.$refs.reportView) {
                this.$refs.reportView.executeReport()
            }
        },

        onFiltersLoaded({ definitions, values }) {
            this.filterDefinitions = definitions || []
            this.filterValues = { ...values }
        },

        // Applicazione live: il menu resta aperto per continuare a regolare i filtri
        applyFilters() {
            if (this.$refs.reportView) {
                this.$refs.reportView.applyFilters(this.filterValues)
            }
        }
    },

    watch: {
        'widget.w'() {
            // Ricrea il grafico quando la larghezza cambia
            if (this.$refs.reportView && this.$refs.reportView.chartData?.type && this.$refs.reportView.chartData.type !== 'counter') {
                this.$nextTick(() => {
                    if (this.$refs.reportView) {
                        this.$refs.reportView.createChart()
                    }
                })
            }
        },
        'widget.h'() {
            // Ricrea il grafico quando l'altezza cambia
            if (this.$refs.reportView && this.$refs.reportView.chartData?.type && this.$refs.reportView.chartData.type !== 'counter') {
                this.$nextTick(() => {
                    if (this.$refs.reportView) {
                        this.$refs.reportView.createChart()
                    }
                })
            }
        }
    }
}
</script>

<style scoped>
.widget-header {
    min-height: 40px;
    background: rgba(0, 0, 0, 0.02);
}

.theme--dark .widget-header {
    background: rgba(255, 255, 255, 0.05);
}

.widget-title {
    max-width: 200px;
}

.widget-content {
    min-height: 100px;
}

.filter-chips-row {
    overflow: hidden;
}

.filter-chip {
    max-width: 220px;
    cursor: pointer;
    flex-shrink: 0;
}

.filter-chip >>> .v-chip__content {
    overflow: hidden;
    text-overflow: ellipsis;
}

/* Dark mode support for widget card */
.theme--dark .v-card {
    background-color: #1E1E1E !important;
}
</style>
